<script>
import 'intersection-observer';
import scrollama from 'scrollama';
import debounce from 'lodash/debounce';

export default {
  name: 'TheAnchorBar',
  props: {
    label: {
      type: String,
      default: 'Jump to',
    },
  },
  data() {
    return {
      currentIndex: null,
      scrollama: scrollama(),
      targets: [],
      isStuck: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.populateTargets();
      this.setScrollamaSteps();
      window.addEventListener('resize', debounce(() => this.resize(), 300));
    });
  },
  updated() {
    document.addEventListener('sticky-change', (e) => {
      this.isStuck = e.detail.stuck;
    });
    this.observeHeaders();
  },
  methods: {
    fire(stuck) {
      const evt = new CustomEvent('sticky-change', { detail: { stuck } });
      document.dispatchEvent(evt);
    },
    observeHeaders() {
      const observer = new IntersectionObserver((records) => {
        records.forEach((record) => {
          const targetInfo = record.boundingClientRect;
          const rootBoundsInfo = record.rootBounds;

          if (targetInfo.bottom < rootBoundsInfo.top) {
            this.fire(true);
          }

          if (targetInfo.bottom >= rootBoundsInfo.top
            && targetInfo.bottom < rootBoundsInfo.bottom) {
            this.fire(false);
          }
        });
      }, {
        threshold: [0],
      });

      // Attach an observer to top sentinel
      observer.observe(document.querySelector('.anchor-bar__sentinel--top'));
    },
    populateTargets() {
      this.targets = Array.from(this.$el.closest('.anchor-bar').querySelectorAll('[data-anchor]'));
    },
    setScrollamaSteps() {
      this.scrollama
        .setup({
          step: this.targets,
          offset: 0.5,
        })
        .onStepEnter((response) => {
          if (response.direction === 'down') {
            this.currentIndex = response.index;
          }
        })
        .onStepExit((response) => {
          if (response.direction === 'up') {
            this.currentIndex = response.index === 0 ? null : response.index - 1;
          }
        });
    },
    resize() {
      this.scrollama.resize();
    },
    scrollTo(target, index) {
      const scrollTop = window.pageYOffset + Math.round(target.getBoundingClientRect().top);

      if (index !== this.currentIndex) {
        target.focus();
        window.scrollTo({
          behavior: 'smooth',
          top: index < this.currentIndex
            ? scrollTop - this.$el.clientHeight - document.querySelector('header').clientHeight
            : scrollTop - this.$el.clientHeight,
        });
      }
    },
  },
  computed: {
    formattedCurrentIndex() {
      return this.currentIndex !== null && !this.targets[this.currentIndex].dataset.highlight
        ? this.currentIndex
        : 'null';
    },
  },
};
</script>
<template>
  <nav
    class="anchor-bar__bar"
    :class="[{ 'stuck': isStuck }]"
  >
    <div class="anchor-bar__dropdown">
      <label
        class="control__label sr-only"
        for="anchor-bar-select"
      >{{ label }}</label>
      <select
        id="anchor-bar-select"
        :value="formattedCurrentIndex"
        class="anchor-bar__select"
        @change="scrollTo(targets[$event.target.value], $event.target.value)"
      >
        <option
          value="null"
          disabled
        >
          {{ label }}
        </option>
        <option
          v-for="(target, index) in targets"
          :value="index"
          :key="target.id"
        >
          {{ target.dataset.anchor }}
        </option>
      </select>
    </div>
    <div class="anchor-bar__grid">
      <ul class="anchor-bar__list">
        <li
          v-for="(target, index) in targets"
          :key="target.id"
        >
          <a
            :class="{ 'anchor-bar__link--active': index === currentIndex }"
            :href="'#' + target.id"
            class="anchor-bar__link"
            @click.prevent="scrollTo(target, index)"
          >
            {{ target.dataset.anchor }}
          </a>
        </li>
      </ul>
    </div>
  </nav>
</template>
